import React, { ReactElement } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Theme } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { StyledProps } from "@mui/styles/styled";
import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Avatar,
} from "@mui/material";
import notFoundImage from "../assets/images/not_found.png";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {RoleName, useAllowedRoutes, useSession} from "../lib/session";
import getUserRoles from "../lib/rbac";
import NavLink from "../components/NavLink";
import { menuRoutes, adminRoutes, viewerMenuRoutes, monitorMenuRoutes } from "../routes";
import { useLocation } from "react-router-dom";
import { palette, typography } from "../theme/cranecastTheme";
import { useAuth0 } from "../components/react-auth0-spa";
import { Settings } from "lucide-react";

interface StyleProps {
  projectImage?: string;
}
const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    backgroundColor: palette.background.default,
    height: '100%',
    borderRight: `1px solid ${palette.grey[200]}`,
    display: 'flex',
    flexDirection: 'column',
  },
  sidebar: {
    scrollbarWidth: "none",
    flex: 1,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    paddingBottom: theme.spacing(2),
  },
  project: {
    cursor: "pointer",
    padding: theme.spacing(3),
    backgroundColor: palette.grey[50],
    borderBottom: `1px solid ${palette.grey[200]}`,
    transition: 'background-color 0.2s',
    "&:hover": {
      backgroundColor: palette.grey[100],
    },
  },
  projectAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  projectIcon: {
    paddingRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
  projectName: {
    ...typography.h6,
    color: palette.text.primary,
    letterSpacing: '-0.025em',
  },
  MuiAccordionroot: {
    backgroundColor: 'transparent',
    boxShadow: "none",
    "&.MuiAccordion-root:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordionSummary-root": {
      padding: theme.spacing(1.5, 3),
      minHeight: '48px',
      "&:hover": {
        backgroundColor: palette.grey[50],
      },
    },
    "& .MuiAccordionSummary-content": {
      margin: '0',
    },
    "& .MuiAccordionSummary-content.Mui-expanded": {
      margin: '0',
    },
    "& .MuiAccordionDetails-root": {
      padding: theme.spacing(0, 3, 1, 7),
    },
  },
  bottomDivider: {
    display: 'none', // Remove dividers for cleaner look
  },
  list: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: 0,
    [theme.breakpoints.up("lg")]: {
      height: "auto",
    },
  },
  listItem: {
    cursor: "pointer",
    borderRadius: '8px',
    padding: theme.spacing(1.5, 2),
    transition: 'all 0.2s',
    marginBottom: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: palette.grey[100],
      "& $listItemText": {
        color: palette.text.primary,
      },
      "& $listItemIcon": {
        color: palette.text.primary,
      },
    },
  },
  activeListItem: {
    backgroundColor: palette.grey[50],
    borderLeft: `3px solid ${palette.secondary.main}`,
    paddingLeft: theme.spacing(1.7), // Adjust padding to account for border
    "&:hover": {
      backgroundColor: palette.grey[100],
    },
    "& $listItemText": {
      fontWeight: 600,
      color: palette.text.primary,
    },
    "& $listItemIcon": {
      color: palette.secondary.main,
    },
  },
  parentMenu: {
    ...typography.subtitle2,
    color: palette.text.secondary,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.2s',
  },
  activeParentMenu: {
    color: palette.text.primary,
    '& $listItemIcon': {
      color: palette.secondary.main,
    },
  },
  parentOverride: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  listItemIcon: {
    marginRight: theme.spacing(1.5),
    minWidth: 'auto',
    color: palette.text.secondary,
    display: 'inline-flex',
    transition: 'color 0.2s',
    '& svg': {
      height: 20,
      width: 20,
    }
  },
  listItemText: {
    ...typography.body2,
    margin: 0,
    '& .MuiTypography-root': {
      ...typography.body2,
      fontWeight: 500,
      color: palette.text.secondary,
    },
  },
  iconColor: {
    color: palette.text.secondary,
    transition: 'transform 0.2s',
  },
  logoutSection: {
    padding: theme.spacing(3),
    borderTop: `1px solid ${palette.grey[200]}`,
  },
  userProfile: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2),
    backgroundColor: palette.grey[50],
    borderRadius: '8px',
    border: `1px solid ${palette.grey[200]}`,
  },
  userLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  userIcon: {
    fontSize: '16px',
    color: palette.text.secondary,
    flexShrink: 0,
  },
  userEmail: {
    ...typography.body2,
    color: palette.text.primary,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  settingsButton: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5, 2),
    borderRadius: '8px',
    ...typography.button,
    textTransform: 'none',
    color: palette.text.secondary,
    backgroundColor: 'transparent',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: palette.grey[50],
      color: palette.text.primary,
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1.5),
    },
  },
  logoutButton: {
    width: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5, 2),
    borderRadius: '8px',
    ...typography.button,
    textTransform: 'none',
    color: palette.primary.main,
    backgroundColor: palette.secondary.main,
    fontWeight: 600,
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: palette.secondary.dark,
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1.5),
    },
  }
}));

interface ItemProps {
  icon?: ReactElement;
  label: string;
  component?: ReactElement;
}

interface MenuProps {
  name: string;
  icon?: ReactElement;
  path: string;
}

interface NavItemProps extends ItemProps {
  path: string;
}

// Helper function to format route names to Proper Case
const toProperCase = (str: string) => {
  // Handle special cases
  if (str.toLowerCase() === 'jibtracker') return 'Jibtracker';
  
  // Split by spaces and capitalize each word
  return str.split(' ').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const NavItem = ({ path, label, icon }: NavItemProps) => {
  const classes = useStyles({});
  return (
    <ListItem
      activeClassName={classes.activeListItem}
      className={classes.listItem}
      component={NavLink}
      to={path}
      exact
    >
      {icon && <ListItemIcon className={classes.listItemIcon}>{icon}</ListItemIcon>}
      <ListItemText
        classes={{ primary: classes.listItemText }}
        primary={toProperCase(label)}
      />
    </ListItem>
  );
};

type Props = {
  setOpen: (open: boolean) => void;
} & StyledProps;

export default function Sidebar ({ className, setOpen }: Props): JSX.Element {
  const { t } = useTranslation();
  const { activeProject } = useSession();
  const userRoles: RoleName[] = getUserRoles(activeProject);
  const isViewer = userRoles.includes(RoleName.VIEWER);
  const isMonitor = userRoles.includes(RoleName.MONITOR);
  const isSiteAdmin = userRoles.includes(RoleName.SITEADMIN);
  const location = useLocation();
  const { logout, user } = useAuth0();
  
  // Check if user has settings permission
  const hasSettingsPermission = isSiteAdmin || userRoles.includes(RoleName.ADMIN); // Settings requires SITEADMIN or ADMIN role
  
  const handleLogout = () => {
    localStorage.removeItem("token");
    logout({ returnTo: window.location.origin });
  };

  const allowedNormalRoutes = useAllowedRoutes(
    activeProject.id,
    menuRoutes,
    userRoles
  );
  const allowedAdminRoutes = useAllowedRoutes(
    activeProject.id,
    adminRoutes,
    userRoles
  );
  const allowedViewerRoutes = useAllowedRoutes(
    activeProject.id,
    viewerMenuRoutes,
    userRoles
  )
  const allowedMonitorRoutes = useAllowedRoutes(
    activeProject.id,
    monitorMenuRoutes,
    userRoles
  )
  // TODO: Remove isViewer clause once we solidify future RBAC approach
  const routes = isViewer
    ? allowedViewerRoutes
    : isMonitor
      ? allowedMonitorRoutes
      : allowedNormalRoutes.concat(allowedAdminRoutes);
  const groupedRoutes = Array.from(
    routes
      .reduce((r, o) => {
        const key = o.parentMenu;
        const item =
          r.get(key) ||
          Object.assign({}, o, {
            allowedRoutes: [],
            parentOverride: false,
            parentIcon: null,
          });
        item.allowedRoutes.push(o);
        item.parentOverride = o.parentOverride;
        o.parentIcon && (item.parentIcon = o.parentIcon);
        return r.set(key, item);
      }, new Map())
      .values()
  ).map((m) => {
    return {
      parentMenu: m.parentMenu,
      parentOverride: m.parentOverride,
      parentIcon: m.parentIcon,
      allowedRoutes: m.allowedRoutes,
    };
  });
  const classes = useStyles({ projectImage: activeProject.image });
  return (
    <nav className={classNames(classes.root, className)}>
      <div className={classes.sidebar}>
        <ListItem
          className={classes.project}
          component={NavLink}
          to={"/projects"}
          exact
          onClick={() => setOpen(false)}
        >
          <Tooltip title="Switch projects" placement="right">
            <ListItemIcon className={classes.projectIcon}>
              <Avatar variant="rounded" className={classes.projectAvatar} src={activeProject.image ?? notFoundImage} />
            </ListItemIcon>
          </Tooltip>
          <ListItemText
            classes={{ primary: classes.projectName }}
            primary={activeProject.name}
          />
        </ListItem>
        <Divider className={classes.bottomDivider} />
        {groupedRoutes.map((g) => {
          const isParentActive = g.allowedRoutes.some((route: MenuProps) => location.pathname === route.path);
          return (
          g.parentOverride ? (
            <div className={classes.parentOverride} key={g.parentMenu}>
              {g.allowedRoutes.map(({ name, path, icon }: MenuProps) => (
                <NavItem key={name} path={path} label={t(name)} icon={icon} />
              ))}
            </div>
          ) : (
            <Accordion
              key={g.parentMenu}
              disableGutters
              elevation={0}
              classes={{
                root: classes.MuiAccordionroot,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon className={classes.iconColor} />}>
                <div className={classNames(classes.parentMenu, { [classes.activeParentMenu]: isParentActive })}>
                  {g.parentIcon && <ListItemIcon className={classes.listItemIcon}>
                    {g.parentIcon}
                  </ListItemIcon>}
                  {toProperCase(g.parentMenu)}
                </div>
              </AccordionSummary>
              <AccordionDetails
                classes={{
                  root: classes.MuiAccordionroot,
                }}
              >
                <List
                  className={classes.list}
                  component="div"
                  disablePadding
                  key={g.parentMenu}
                >
                  {g.allowedRoutes.map(({ name, path }: MenuProps) => (
                    <NavItem
                      key={name}
                      path={path}
                      label={t(name)}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )
        )})}
      </div>
      <div className={classes.logoutSection}>
        {hasSettingsPermission && (
          <Button
            startIcon={<Settings />}
            component={NavLink}
            to="/settings"
            className={classes.settingsButton}
            onClick={() => setOpen(false)}
          >
            {toProperCase(t("settings"))}
          </Button>
        )}
        {user?.email && (
          <div className={classes.userProfile}>
            <div className={classes.userLabel}>
              <PersonOutlineIcon className={classes.userIcon} />
              <div className={classes.userEmail}>
                {user.email}
              </div>
            </div>
          </div>
        )}
        <Button
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          className={classes.logoutButton}
        >
          {toProperCase(t("logout"))}
        </Button>
      </div>
    </nav>
  );
};
